import { useMemo, type ReactElement } from 'react'
import {
  buildOcrCallStats,
  formatOcrDurationMs,
  type OcrTimelineEvent,
} from '../utils/buildOcrCallStats'

type OcrCallStatsSummaryProps = {
  events: OcrTimelineEvent[]
}

export function OcrCallStatsSummary({
  events,
}: OcrCallStatsSummaryProps): ReactElement | null {
  const stats = useMemo(() => buildOcrCallStats(events), [events])

  if (!stats) {
    return null
  }

  const showLatency = stats.finishedCount > 0

  return (
    <div className='space-y-2 border-b px-3 py-3'>
      <div>
        <h3 className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
          OCR call stats
        </h3>
        <p className='text-[11px] text-muted-foreground'>
          {stats.finishedCount} finished
          {stats.failedCount > 0 ? ` · ${stats.failedCount} failed` : ''}
          {stats.openCount > 0 ? ` · ${stats.openCount} still open` : ''}
          {showLatency
            ? ' · latency from finished calls only'
            : ' · no finished calls yet'}
        </p>
      </div>

      {showLatency && (
        <dl className='grid grid-cols-2 gap-2 sm:grid-cols-4'>
          <div className='rounded-md border bg-muted/20 px-3 py-2'>
            <dt className='text-[11px] text-muted-foreground'>Longest</dt>
            <dd className='text-sm font-semibold tabular-nums'>
              {formatOcrDurationMs(stats.longestMs)}
            </dd>
            <dd className='text-[11px] text-muted-foreground'>page {stats.longestPage}</dd>
          </div>
          <div className='rounded-md border bg-muted/20 px-3 py-2'>
            <dt className='text-[11px] text-muted-foreground'>Average</dt>
            <dd className='text-sm font-semibold tabular-nums'>
              {formatOcrDurationMs(stats.averageMs)}
            </dd>
          </div>
          <div className='rounded-md border bg-muted/20 px-3 py-2'>
            <dt className='text-[11px] text-muted-foreground'>p95</dt>
            <dd className='text-sm font-semibold tabular-nums'>
              {formatOcrDurationMs(stats.p95Ms)}
            </dd>
          </div>
          <div className='rounded-md border bg-muted/20 px-3 py-2'>
            <dt className='text-[11px] text-muted-foreground'>p99</dt>
            <dd className='text-sm font-semibold tabular-nums'>
              {formatOcrDurationMs(stats.p99Ms)}
            </dd>
          </div>
        </dl>
      )}
    </div>
  )
}
